import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Loader2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { company_name: '', contact_name: '', email: '', staff_count: '', plan: 'annual', invoice_number: '', website: '', amount: '', term_months: '12', featured_months: '0', inclusions: '' };
const input = 'w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00A79D]';

const Row = ({ label, children }) => <label className="block"><span className="text-xs font-semibold text-gray-600">{label}</span><div className="mt-1">{children}</div></label>;

const Done = ({ r, onClose }) => (
  <div className="space-y-3 text-sm" data-testid="invoiced-done">
    <p className="text-gray-800"><b>{r.reused_existing ? 'Existing supplier set up' : 'Supplier created'}</b> as {r.band_label} ({r.staff_label} staff), £{r.amount} for {r.term_months} months{r.featured_months ? ` with ${r.featured_months} month${r.featured_months > 1 ? 's' : ''} featured` : ''}, marked paid by invoice.</p>
    <p className="text-gray-600">{r.email_status === 'sent' ? 'The set-password email has been sent.' : 'The set-password email is queued.'} Their listing will arrive in Listing Approvals when they press Submit for review.</p>
    <div className="flex gap-2">
      <input readOnly value={r.set_password_url} className={`${input} text-xs`} data-testid="invoiced-link" />
      <button type="button" onClick={() => { navigator.clipboard.writeText(r.set_password_url); toast.success('Link copied'); }} className="px-3 border rounded-md hover:bg-gray-50" data-testid="invoiced-copy"><Copy className="w-4 h-4" /></button>
    </div>
    <button type="button" onClick={onClose} className="w-full bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold rounded-md py-2 transition-colors" data-testid="invoiced-close">Done</button>
  </div>
);

export const AddInvoicedSupplier = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const close = () => { setOpen(false); setRes(null); setF(EMPTY); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/directory/invoiced-supplier`, { ...f, staff_count: Number(f.staff_count), amount: f.amount === '' ? null : Number(f.amount), term_months: Number(f.term_months) || 12, featured_months: Number(f.featured_months) || 0 });
      setRes(data); onCreated?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add supplier'); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="add-invoiced-supplier-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold rounded-lg shrink-0 transition-colors">
        <FileText className="w-4 h-4" />Add invoiced supplier
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoiced-dialog">
          <DialogHeader>
            <DialogTitle>Add invoiced supplier</DialogTitle>
            <DialogDescription>For suppliers who've paid by invoice. Creates their draft listing and emails them a link to finish it.</DialogDescription>
          </DialogHeader>
          {res ? <Done r={res} onClose={close} /> : (
            <form onSubmit={submit} className="space-y-3">
              <Row label="Company name"><input required value={f.company_name} onChange={set('company_name')} className={input} data-testid="invoiced-company" /></Row>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Contact name"><input value={f.contact_name} onChange={set('contact_name')} className={input} data-testid="invoiced-contact" /></Row>
                <Row label="Contact email"><input required type="email" value={f.email} onChange={set('email')} className={input} data-testid="invoiced-email" /></Row>
              </div>
              <Row label="Website (optional)"><input value={f.website} onChange={set('website')} className={input} data-testid="invoiced-website" /></Row>
              <div className="grid grid-cols-3 gap-3">
                <Row label="Staff"><input required type="number" min="1" value={f.staff_count} onChange={set('staff_count')} className={input} data-testid="invoiced-staff" /></Row>
                <Row label="Plan">
                  <select value={f.plan} onChange={set('plan')} className={input} data-testid="invoiced-plan">
                    <option value="annual">Annual</option><option value="monthly">Monthly</option>
                  </select>
                </Row>
                <Row label="Invoice no."><input required value={f.invoice_number} onChange={set('invoice_number')} className={input} data-testid="invoiced-invoice" /></Row>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Row label="Invoice total (£)"><input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} placeholder="Band price" className={input} data-testid="invoiced-amount" /></Row>
                <Row label="Listing length (months)"><input type="number" min="1" max="60" value={f.term_months} onChange={set('term_months')} className={input} data-testid="invoiced-term" /></Row>
                <Row label="Featured (months)"><input type="number" min="0" max="60" value={f.featured_months} onChange={set('featured_months')} className={input} data-testid="invoiced-featured" /></Row>
              </div>
              <Row label="What the listing includes"><textarea rows={3} value={f.inclusions} onChange={set('inclusions')} placeholder={'e.g. 12-month listing\n1 month featured listing\nNewsletter mention in March'} className={input} data-testid="invoiced-inclusions" /></Row>
              <p className="text-[11px] text-gray-500">Featured months start the day the listing is approved and goes live; featured suppliers show first in the directory.</p>
              <button type="submit" disabled={busy} data-testid="invoiced-submit"
                className="w-full inline-flex justify-center items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold rounded-md py-2.5 transition-colors disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create supplier &amp; send link
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
